/**
 * A popup's material (menus, flyouts, pockets, control tooltips): the app's
 * glass, or the wall card's paper (Part 09 review). A surface declares the
 * material of every popup opened from inside it with
 * `MenuSurfaceContext.Provider` (the wall provides "card"); React context
 * follows the popups through their portals, so the menu, its flyouts and
 * the rows inside all agree. A popup may still name its own.
 */
import { createContext, useContext } from "react";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
  type NativeSurfaceOptions,
} from "./surface-contract";

export type MenuSurface = "glass" | "card";

export const MenuSurfaceContext = createContext<MenuSurface>("glass");

/** The material a popup wears: its own, or the one its surface declares. */
export function useMenuSurface(own?: MenuSurface): MenuSurface {
  const declared = useContext(MenuSurfaceContext);
  return own ?? declared;
}

/**
 * The class and attributes of a menu-like popup. Glass: the menu surface
 * role, registered for native glass with `native`. Card: the wall card's
 * paper, shadow, radius and text (`.denker-card-menu` in index.css),
 * painted by CSS in every theme and never a native plate.
 */
export function menuSurfaceProps(
  surface: MenuSurface,
  native: NativeSurfaceOptions = {},
): {
  className: string;
  attributes: Record<string, string>;
} {
  if (surface === "card") {
    return {
      className: "denker-card-menu",
      attributes: { "data-denker-glass-force": "css" },
    };
  }
  return {
    className: surfaceRoleClassName("menu"),
    attributes: surfaceRoleAttributes("menu", native),
  };
}
